import type { EmblaCarouselType, EmblaEventType, EmblaOptionsType } from "embla-carousel"
import type { AutoScrollOptionsType } from "embla-carousel-auto-scroll"

import * as Icon from "@iyk/icons"
import * as React from "react"

import AutoScroll from "embla-carousel-auto-scroll"
import Autoplay, { type AutoplayOptionsType } from "embla-carousel-autoplay"
import useEmblaCarousel from "embla-carousel-react"

import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures"
import { cva, cx } from "./cva.ts"
import { Button } from "./forms.ts"
import { useResponsiveCSSVariables, type ResponsiveProp } from "./use-responsive-css-variables.ts"

const noopCarousel: CarouselV2.CarouselAPI = {
  goTo: () => {},
  goToPreviousSlide: () => {},
  goToNextSlide: () => {},
  selectedScrollSnap: () => 0,
  scrollSnapList: () => [],
  onSelect: () => {},
  onReInitialize: () => {},
  onSlidesInView: () => {},
  onIndicatorsInView: () => {},
  canGoToPreviousSlide: () => false,
  canGoToNextSlide: () => false,
}

function createEmblaCarousel(api: EmblaCarouselType): CarouselV2.CarouselAPI {
  return {
    goTo: (index) => api.scrollTo(index),
    goToPreviousSlide: api.scrollPrev,
    goToNextSlide: api.scrollNext,
    selectedScrollSnap: api.selectedScrollSnap,
    scrollSnapList: api.scrollSnapList,
    onSelect: (callback) => api.on("select", callback),
    onReInitialize: (callback) => api.on("reInit", callback),
    onSlidesInView: (callback) => api.on("slidesInView", callback),
    onIndicatorsInView: (callback) => api.on("slidesInView", callback),
    canGoToPreviousSlide: () => api.canScrollPrev(),
    canGoToNextSlide: () => api.canScrollNext(),
  }
}

const CarouselContext = React.createContext<CarouselV2.Context>({
  ...noopCarousel,
  ref: () => {},
  indicatorsRef: () => {},
  indicatorsNotInView: [],
})

const useCarousel = () => {
  const context = React.use(CarouselContext)
  if (context === null) {
    throw new Error("useCarousel must be used within a Carousel.Root")
  }
  return context
}

const Root = ({
  children,
  onSlideChange,
  onSlideClick,
  options = { align: "center", containScroll: "trimSnaps" },
  slideSpacing = 20,
  slideSize = "100%",
  indicatorSpacing = 4,
  indicatorSize = "6%",
  className,
}: CarouselV2.Root) => {
  const id = React.useId()
  const carouselRootId = `carousel-root-${id}`

  const { autoPlay, autoScroll, ...carouselOptions } = options
  const direction = options.axis ? (options.axis === "x" ? "horizontal" : "vertical") : "horizontal"

  const { responsiveStyleTag } = useResponsiveCSSVariables({
    cssVars: {
      "--slide-spacing": slideSpacing,
      "--slide-size": slideSize,
      "--indicator-spacing": indicatorSpacing,
      "--indicator-size": indicatorSize,
    },
    selector: `[data-role="${carouselRootId}"]`,
  })

  const [ref, api] = useEmblaCarousel(carouselOptions, [
    WheelGesturesPlugin(),
    AutoScroll({
      active: Boolean(autoScroll),
      ...autoScroll,
    }),
    Autoplay({
      active: Boolean(autoPlay),
      ...autoPlay,
    }),
  ])
  const [indicatorsRef, indicatorsApi] = useEmblaCarousel(
    {
      containScroll: "keepSnaps",
      dragFree: true,
    },
    [],
  )

  const [_, setSelectedSnap] = React.useState(0)

  const [indicatorsNotInView, setIndicatorsNotInView] = React.useState<Array<number>>([])

  const carousel: CarouselV2.CarouselAPI = api ? createEmblaCarousel(api) : noopCarousel
  const indicators: CarouselV2.CarouselAPI = indicatorsApi
    ? createEmblaCarousel(indicatorsApi)
    : noopCarousel

  const updateSnap = (api: EmblaCarouselType) => {
    const newSnap = api.selectedScrollSnap()
    setSelectedSnap(newSnap)
    indicators.goTo(newSnap)
    onSlideChange?.(newSnap)
  }

  carousel.onReInitialize((api) => api.scrollSnapList)
  carousel.onReInitialize(updateSnap)
  carousel.onSelect(updateSnap)

  indicators.onIndicatorsInView((api) => setIndicatorsNotInView(api.slidesNotInView()))

  const showCarousel = Boolean(carousel.scrollSnapList().length)

  return (
    <CarouselContext
      value={{
        ...carousel,
        ref,
        indicatorsRef,
        indicatorsNotInView,
        onSlideClick,
      }}
    >
      {responsiveStyleTag}
      <div
        className={cx(
          "flex flex-col relative group",
          "opacity-0 data-[show=true]:opacity-100",
          "transition-opacity duration-300",
          className,
        )}
        data-role={carouselRootId}
        data-show={showCarousel}
        data-is-vertical={direction === "vertical"}
      >
        {children}
      </div>
    </CarouselContext>
  )
}

const Slides = <T,>({ slides, children, classNames, ...props }: CarouselV2.Slides<T>) => {
  const { ref } = useCarousel()
  return (
    <div
      ref={ref}
      className={cx("overflow-hidden", classNames?.viewport)}
      data-role="carousel-slides-viewport"
      {...props}
    >
      <div
        className={cx(
          "flex group-data-[is-vertical=true]:h-[calc(100%+var(--slide-spacing))] group-data-[is-vertical=false]:h-full",
          "group-data-[is-vertical=true]:flex-col group-data-[is-vertical=true]:-mt-[var(--slide-spacing)]",
          "group-data-[is-vertical=false]:-ml-[var(--slide-spacing)]",
          "group-data-[is-vertical=true]:[touch-action:touch-manipulation_pinch-zoom] group-data-[is-vertical=false]:[touch-action:touch-manipulation_pinch-zoom]",
          classNames?.container,
        )}
        data-role="carousel-slides-container"
      >
        {slides.map((slide, index) => (
          <React.Fragment key={index}>{children(slide, index)}</React.Fragment>
        ))}
      </div>
    </div>
  )
}

const Slide = ({ children, className, ...props }: CarouselV2.Slide) => {
  return (
    <section
      data-role="carousel-slide"
      className={cx(
        "min-w-0 select-none",
        "group-data-[is-vertical=true]:pt-[var(--slide-spacing)]",
        "group-data-[is-vertical=false]:pl-[var(--slide-spacing)]",
        "[transform:translate3d(0,0,0)]",
        "flex flex-col justify-center",
        "flex-[0_0_var(--slide-size)]",
        className,
      )}
      {...props}
    >
      {children}
    </section>
  )
}

const Indicators = ({ variant, classNames, ...props }: CarouselV2.Indicators) => {
  const { scrollSnapList, indicatorsRef, indicatorsNotInView } = useCarousel()

  const lines = scrollSnapList()

  return (
    <div
      ref={indicatorsRef}
      className={cx("overflow-hidden", classNames?.viewport)}
      data-role="carousel-indicators-viewport"
      {...props}
    >
      <div
        className={classForIndicators({ variant, className: classNames?.container })}
        data-role="carousel-indicator-container"
        data-centered={!Boolean(indicatorsNotInView.length)}
      >
        {lines.map((_, index) => (
          <Indicator key={index} index={index} variant={variant} />
        ))}
      </div>
    </div>
  )
}

const classForIndicators = cva(
  ["flex gap-2 -ml-[var(--indicator-spacing)]", "data-[centered=true]:justify-center group"],
  {
    variants: {
      variant: {
        dots: "items-center h-6",
        "vertical-lines": "items-end h-8",
        "horizontal-lines": "items-center h-6",
      },
    },
  },
)

const Indicator = ({ index, variant }: CarouselV2.Indicator) => {
  const { goTo, selectedScrollSnap } = useCarousel()
  const selectedLine = selectedScrollSnap()
  const isActive = selectedLine === index

  return (
    <div
      role="button"
      onClick={() => goTo(index)}
      className={cx(
        "relative",
        "group-data-[centered=false]:flex-[0_0_var(--indicator-size)]",
        "group-data-[centered=false]:min-w-0",
        "pl-[var(--indicator-spacing)]",
        "before:content-[''] before:absolute before:z-[-1]",
        "before:inset-[-4px]", // To increase the clickable area
      )}
    >
      <div className={classForIndicator({ variant })} data-active={isActive} />
    </div>
  )
}

const classForIndicator = cva(
  ["bg-gray-12 data-[active=true]:opacity-100", "transition-opacity ease-in-out"],
  {
    variants: {
      variant: {
        dots: [
          "size-1 md:size-2 rounded-full",
          "data-[active=false]:bg-gray-1",
          "data-[active=false]:border data-[active=false]:border-gray-8",
        ],
        "vertical-lines": ["h-4 w-px opacity-30", "data-[active=true]:h-5"],
        "horizontal-lines": ["w-4 h-px opacity-30", "data-[active=true]:w-5"],
      },
    },
  },
)

const LeftArrow = ({ kind = "ghost", size = "sm", className, ...props }: CarouselV2.LeftArrow) => {
  const { goToPreviousSlide } = useCarousel()
  return (
    <Button
      type="button"
      kind={kind}
      size={size}
      onClick={() => goToPreviousSlide()}
      className={className}
      {...props}
    >
      <Icon.ChevronLeft />
    </Button>
  )
}

const RightArrow = ({
  kind = "ghost",
  size = "sm",
  className,
  ...props
}: CarouselV2.RightArrow) => {
  const { goToNextSlide } = useCarousel()
  return (
    <Button
      type="button"
      kind={kind}
      size={size}
      onClick={() => goToNextSlide()}
      className={className}
      {...props}
    >
      <Icon.ChevronRight />
    </Button>
  )
}

const Counter = ({ className, ...props }: CarouselV2.Counter) => {
  const { selectedScrollSnap, scrollSnapList } = useCarousel()
  return (
    <span className={cx("text-xs text-gray-12", className)} {...props}>
      {selectedScrollSnap() + 1} / {scrollSnapList().length}
    </span>
  )
}

export namespace CarouselV2 {
  type CarouselRef = <ViewportElement extends HTMLElement>(instance: ViewportElement | null) => void
  type CallbackType = (emblaApi: EmblaCarouselType, evt: EmblaEventType) => void
  type IndicatorVariant = "dots" | "vertical-lines" | "horizontal-lines"

  export type Context = CarouselAPI & {
    ref: CarouselRef
    indicatorsRef: CarouselRef
    onSlideClick?: (index: number) => void
    indicatorsNotInView: Array<number>
  }
  export type CarouselAPI = {
    goTo: (index: number) => void
    goToPreviousSlide: () => void
    goToNextSlide: () => void
    selectedScrollSnap: () => number
    scrollSnapList: () => number[]
    onSelect: (callback: CallbackType) => void
    onReInitialize: (callback: CallbackType) => void
    onSlidesInView: (callback: CallbackType) => void
    onIndicatorsInView: (callback: CallbackType) => void
    canGoToPreviousSlide: () => boolean
    canGoToNextSlide: () => boolean
  }

  export type Root = {
    children: React.ReactNode
    onSlideChange?: (index: number) => void
    onSlideClick?: (index: number) => void
    slideSpacing?: ResponsiveProp<number>
    slideSize?: ResponsiveProp<`${number}%` | `calc(${string})` | "auto">
    indicatorSpacing?: ResponsiveProp<number>
    indicatorSize?: ResponsiveProp<`${number}%`>
    className?: string
    options?: EmblaOptionsType & {
      autoPlay?: AutoplayOptionsType
      autoScroll?: AutoScrollOptionsType
    }
  }

  export type Slides<T> = Omit<React.ComponentProps<"div">, "className" | "ref" | "children"> & {
    slides: T[]
    children: (data: T, index: number) => React.ReactNode
    classNames?: {
      viewport?: string
      container?: string
    }
  }
  export type Slide = React.ComponentProps<"section">
  export type Indicators = Omit<React.ComponentProps<"div">, "children" | "className"> & {
    variant: IndicatorVariant
    classNames?: {
      viewport?: string
      container?: string
    }
  }
  export type Indicator = {
    index: number
    variant: IndicatorVariant
  }
  export type LeftArrow = Omit<React.ComponentProps<typeof Button>, "type">
  export type RightArrow = Omit<React.ComponentProps<typeof Button>, "type">
  export type Counter = React.ComponentProps<"span">
}

export const CarouselV2 = {
  Root,
  Slide,
  Slides,
  useCarousel,
  Indicators,
  LeftArrow,
  RightArrow,
  Counter,
}
